import { Button, FormControlLabel, IconButton, Popover, Switch, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Filter, useSearchkit } from '@searchkit/client';
import { filter, find } from 'lodash';
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { SkFacetSet } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../../common/auth/UserContext';
import { usePersistedSearchkitVariables } from '../../../common/components/filtering/filters';
import { useFilterFacet } from '../../../common/components/filtering/useFilterFacet';
import { FILTER_IDENTIFIERS } from '../../../common/constants/constants';
import I18n, { useI18n } from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { H, V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import PromotedFacet from './PromotedFacet';
import QuickFilterFacets from './QuickFilterFacets';
import SavedFiltersList from './SavedFiltersList';
import SearchPanel from './SearchPanel';
import { StyledBadge } from './StyledBadge';

type FooterProps = {
    loading?: boolean;
    count?: number;
    immutableFilters?: Filter[];
    requiredFilters?: string[];
    isReporting?: boolean;
    isSavedFilters?: boolean;
    autoApply: boolean;
    hasPendingFilters: boolean;
    onToggleAutoApply: (value: boolean) => void;
    onApply: () => void;
    canApply: boolean;
    onClearAll: () => void;
    onClose: () => void;
    hasUserFilters: boolean;
};

// We want to exclude these filter keys from counting if user has filters or not.
// TODO: Remove 'sr-type' after implementing filters for Activtiy in RM
const EXCLUDE_STATIC = ['entity', 'primary', 'public', 'sr-type'];
const POPOVER_VERTICAL_OFFSET_PX = 162; // Offset for Popover maxHeight to account for header/toolbars spacing. Update this value if surrounding layout changes.

type PromotedFacetsProps = {
    facets?: SkFacetSet[];
    promote?: string[];
    facetLabelProps?: {
        labelMap?: Record<string, string>;
        showLabelInitials?: boolean;
    };
};

const PromotedFacets = ({ facets, promote, facetLabelProps }: PromotedFacetsProps) => {
    if (promote && promote.length && facets) {
        const pf = promote.map((p) => find(facets, (f) => f.identifier === p)).filter((f) => f);
        return pf.map((f, index) => <PromotedFacet key={index} facet={f} facetLabelProps={facetLabelProps} />);
    }
    return null;
};

type HeaderProps = {
    activeTab: number;
    setActiveTab: Dispatch<SetStateAction<number>>;
    enableSaveFilters: boolean;
    count: number;
    isReporting?: boolean;
};

const Header = ({ activeTab, setActiveTab, enableSaveFilters, count, isReporting }: HeaderProps) => {
    const resultsLabel = useI18n('list.results', { count });
    return (
        <V sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <H
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: 'fit-content'
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(e, idx) => setActiveTab(idx)}
                    variant="fullWidth"
                    centered
                    sx={{ minHeight: 54, width: '100%' }}
                    slotProps={{ indicator: { hidden: !enableSaveFilters } }}
                >
                    <Tab
                        label={
                            <H sx={{ alignItems: 'flex-end', gap: 0.5 }}>
                                <I18n token="filter.allFilter.label" />
                                {!isReporting && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 'caption.fontSize',
                                            fontWeight: 'fontWeightRegular',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        ({resultsLabel})
                                    </Typography>
                                )}
                            </H>
                        }
                        sx={{
                            textTransform: 'none',
                            alignItems: enableSaveFilters ? 'center' : 'flex-start',
                            fontSize: 'body1.fontSize'
                        }}
                    />
                    {enableSaveFilters && (
                        <Tab
                            label={<I18n token="filter.savedFilter.label" />}
                            sx={{ textTransform: 'none', fontSize: 'body1.fontSize' }}
                        />
                    )}
                </Tabs>
            </H>
        </V>
    );
};

const Footer = ({
    immutableFilters = [],
    requiredFilters = [],
    autoApply,
    onToggleAutoApply,
    onApply,
    canApply,
    onClearAll,
    onClose,
    hasUserFilters
}: FooterProps) => {
    const t = useI18n();
    const autoApplyLabel = t('filter.autoApply.label');
    const applyLabel = t('filter.apply.label');
    const api = useSearchkit();
    return (
        <V sx={{ borderTop: 1, borderColor: 'divider' }}>
            <H sx={{ p: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <H sx={{ alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                        sx={{ mx: 0 }}
                        control={
                            <Switch
                                size="small"
                                checked={autoApply}
                                onChange={(_, checked) => onToggleAutoApply(checked)}
                            />
                        }
                        label={<Typography sx={{ fontSize: 'caption.fontSize' }}>{autoApplyLabel}</Typography>}
                    />
                </H>
                <H sx={{ alignItems: 'center', gap: 1 }}>
                    <Button
                        size="small"
                        variant="text"
                        disabled={!hasUserFilters}
                        onClick={() => {
                            const entityFilter = find(
                                api.getFilters(),
                                (f) => f.identifier === FILTER_IDENTIFIERS.entity
                            );
                            const publicFilter = find(
                                api.getFilters(),
                                (f) => f.identifier === FILTER_IDENTIFIERS.public
                            );
                            const filterRequired = filter(api.getFilters(), (f) =>
                                requiredFilters.includes(f.identifier)
                            );

                            const filters = [];
                            if (Array.isArray(immutableFilters) && immutableFilters.length > 0) {
                                immutableFilters.forEach((filter) => {
                                    filters.push(filter);
                                });
                            }
                            //  SHOW-3265: adding the filter which are marked as required and shouldn't be cleared.
                            if (Array.isArray(filterRequired) && filterRequired.length > 0) {
                                filterRequired.forEach((filter) => {
                                    filters.push(filter);
                                });
                            }

                            if (entityFilter) {
                                filters.push(entityFilter);
                            }
                            if (publicFilter) {
                                filters.push(publicFilter);
                            }

                            api.setSearchState({
                                ...api.searchState,
                                query: '',
                                filters: filters,
                                page: { from: 0, size: api.searchState.page.size }
                            });
                            onClearAll();
                        }}
                    >
                        <I18n token="facets.clearAll.button" />
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={autoApply || !canApply}
                        onClick={() => {
                            onApply();
                            onClose();
                        }}
                    >
                        {applyLabel}
                    </Button>
                </H>
            </H>
        </V>
    );
};

export default function FacetsPanel({
    id,
    Facets,
    data,
    loading,
    searching = false,
    requiredFilters = [],
    onSearch = undefined,
    promote = undefined,
    hideBadgeCount = false,
    hideAllFilterLabel = false,
    showCount = true,
    isLocal = false,
    isReporting = false,
    labelToken = 'filter.allFilter.label',
    immutableFilters = [],
    facetsVisibility = { filters: true, searchPanel: true, promotedFacets: true, quickFilter: true },
    quickFilters = [],
    searchPlaceholder = undefined,
    resetOnLogout = false,
    preferenceEntityKey = undefined,
    facetLabelProps = {
        labelMap: undefined,
        showLabelInitials: false
    },
    isSavedFilterChangeRef = undefined,
    enableSaveFilters = false,
    defaultFilterAutoApply = true,
    prependViewContent = undefined,
    disabled = false
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const { variables } = usePersistedSearchkitVariables(id);
    const { isMobile, isTablet } = useStyling();
    const count = data && data?.results ? data?.results.hits.page.total : 0;
    const { addOnLogoutListener } = useContext(UserContext);
    const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = Saved
    const hasFacets = !!data?.results?.facets?.length;
    const facetsLoading = loading && !hasFacets;

    const {
        autoApply,
        hasPendingFilters,
        canApply,
        handleFiltersChanged,
        handleToggleAutoApply,
        handleApply,
        handleClearAll
    } = useFilterFacet({
        preferenceEntityKey,
        defaultFilterAutoApply
    });

    const excludeDynamic = useMemo(() => {
        return immutableFilters.map((filter) => {
            return filter.identifier;
        });
    }, [immutableFilters]);
    const filters = variables.filters.filter((f) => {
        return (
            !EXCLUDE_STATIC.includes(f.identifier) &&
            (!promote || !promote.includes(f.identifier)) &&
            (!excludeDynamic || !excludeDynamic.includes(f.identifier))
        );
    });

    const showOnlySearchPanel = useMemo(() => {
        return facetsVisibility.searchPanel && !facetsVisibility.filters && !facetsVisibility.promotedFacets;
    }, [facetsVisibility]);
    const onClose = () => {
        if (!autoApply && hasPendingFilters) {
            handleApply();
        }
        setAnchorEl(null);
    };

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    //clear searchkit variables on logout
    useEffect(() => {
        const clearFiltersFromCache = () => {
            resetOnLogout && window.localStorage.removeItem(`filters_${id}`);
        };

        addOnLogoutListener(clearFiltersFromCache);
    }, [addOnLogoutListener, resetOnLogout, id]);

    return (
        <H className="_facetsPanel" sx={{ gap: 2, width: showOnlySearchPanel ? '100%' : 'unset' }}>
            {facetsVisibility.filters && (
                <StyledBadge badgeContent={hideBadgeCount ? undefined : filters.length}>
                    {!hideAllFilterLabel && !isMobile ? (
                        <Button
                            sx={{
                                whiteSpace: 'nowrap',
                                color: filters.length ? 'success.main' : 'primary.main'
                            }}
                            onClick={onClick}
                            variant={'text'}
                            startIcon={<Icons.Tune />}
                            disabled={disabled}
                        >
                            <I18n token={labelToken} />
                        </Button>
                    ) : (
                        <IconButton
                            sx={{
                                color: filters.length ? 'success.main' : 'primary.main',
                                px: 0.5,
                                py: 0
                            }}
                            onClick={onClick}
                            disabled={disabled}
                        >
                            <Tooltip
                                title={<I18n token={labelToken} />}
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, -6]
                                                }
                                            }
                                        ]
                                    }
                                }}
                            >
                                <Icons.Tune />
                            </Tooltip>
                        </IconButton>
                    )}
                </StyledBadge>
            )}
            {facetsVisibility.searchPanel && (
                <SearchPanel
                    id={`${id}-search`}
                    loading={loading}
                    searching={searching}
                    query={variables.query}
                    setQuery={onSearch}
                    sx={showOnlySearchPanel ? { width: '100%', p: 1 } : {}}
                    searchPlaceholder={searchPlaceholder}
                    isLocal={isLocal}
                />
            )}
            {prependViewContent}
            {facetsVisibility.promotedFacets && (
                <PromotedFacets facets={data?.results?.facets} promote={promote} facetLabelProps={facetLabelProps} />
            )}
            {facetsVisibility.quickFilter && !isMobile && !isTablet && (
                <QuickFilterFacets facets={data?.results?.facets} quickFilters={quickFilters} />
            )}
            {anchorEl && facetsVisibility.filters && (
                <Popover
                    id={`filters-popover-${id}`}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 1,
                                boxSizing: 'border-box',
                                maxWidth: '320px',
                                minWidth: '320px',
                                maxHeight: `calc(100vh - ${POPOVER_VERTICAL_OFFSET_PX}px)`,
                                overflowX: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }
                        }
                    }}
                >
                    <Header
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        enableSaveFilters={enableSaveFilters}
                        count={count}
                        isReporting={isReporting}
                    />
                    {activeTab === 0 ? (
                        <V sx={{ overflowX: 'hidden', px: 1, flexGrow: 2 }}>
                            <Facets
                                facets={data?.results?.facets}
                                loading={facetsLoading}
                                promote={[...(promote || []), ...(excludeDynamic || [])]}
                                showCount={showCount}
                                isLocal={isLocal}
                                requiredFilters={requiredFilters}
                                autoApply={autoApply}
                                onFiltersChanged={handleFiltersChanged}
                            />
                        </V>
                    ) : (
                        <SavedFiltersList
                            preferenceEntityKey={preferenceEntityKey}
                            immutableFilters={immutableFilters}
                            facets={data?.results?.facets}
                            isSavedFilterChangeRef={isSavedFilterChangeRef}
                        />
                    )}
                    <Footer
                        loading={loading}
                        requiredFilters={requiredFilters}
                        count={count}
                        isReporting={isReporting}
                        immutableFilters={immutableFilters}
                        isSavedFilters={activeTab === 1}
                        autoApply={autoApply}
                        hasPendingFilters={hasPendingFilters}
                        onToggleAutoApply={handleToggleAutoApply}
                        onApply={handleApply}
                        canApply={canApply}
                        onClearAll={handleClearAll}
                        onClose={onClose}
                        hasUserFilters={filters.length > 0}
                    />
                </Popover>
            )}
        </H>
    );
}
